// Session handoff: Lead Workspace → salesIntake (turbo_sales.md §11.2 option 1).
// Written before `setMode("salesIntake")`; consumed on the Sales-rep form mount.

import type { SalesRepIntakeDraft } from "@/components/sales-intake/salesRepIntakeBuild";

const STORAGE_KEY = "bb-lead-intake-prefill-v1";

export type LeadIntakePrefillV1 = {
  v: 1;
  lead_id: string;
  /** Active opp_opportunities row for this lead, when one exists (deal-thread link). */
  linked_opportunity_id?: string | null;
  seller_location_id: string | null;
  customer: SalesRepIntakeDraft["customer"];
  notes: string;
};

export function isLeadIntakePrefillV1(value: unknown): value is LeadIntakePrefillV1 {
  if (!value || typeof value !== "object") return false;
  const row = value as Record<string, unknown>;
  return (
    row.v === 1 &&
    typeof row.lead_id === "string" &&
    row.lead_id.trim() !== "" &&
    (row.seller_location_id === null || typeof row.seller_location_id === "string") &&
    typeof row.customer === "object" &&
    row.customer !== null &&
    typeof row.notes === "string"
  );
}

export function setLeadIntakePrefill(payload: LeadIntakePrefillV1): void {
  if (typeof window === "undefined") return;
  try {
    window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
  } catch {
    // sessionStorage may be unavailable
  }
}

export function peekLeadIntakePrefill(): LeadIntakePrefillV1 | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed: unknown = JSON.parse(raw);
    return isLeadIntakePrefillV1(parsed) ? parsed : null;
  } catch {
    return null;
  }
}

export function clearLeadIntakePrefill(): void {
  if (typeof window === "undefined") return;
  try {
    window.sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    // ignore
  }
}

/** Read once and remove — use only when abandoning without the form consuming it. */
export function consumeLeadIntakePrefill(): LeadIntakePrefillV1 | null {
  const payload = peekLeadIntakePrefill();
  if (payload) clearLeadIntakePrefill();
  return payload;
}

export function applyLeadIntakePrefill(
  draft: SalesRepIntakeDraft,
  prefill: LeadIntakePrefillV1,
): SalesRepIntakeDraft {
  return {
    ...draft,
    sellerLocationId: prefill.seller_location_id ?? draft.sellerLocationId,
    customer: { ...draft.customer, ...prefill.customer },
    notes: prefill.notes,
  };
}
